import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssistantService } from 'ts-core';
import { MarkdownMessage } from './MarkdownMessage';

interface AssistantPanelProps {
  endpoints: { assistantConversations: string; assistantChat: string };
  apiKey?: string;
  partnerKey?: string | null;
  balloonId?: string | null;
  onClose: () => void;
}

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * The Beat Engineer chat panel, docked to the right of the map. Streams the
 * assistant's answer over SSE. Read-only — it only ever reads information.
 */
export const AssistantPanel: React.FC<AssistantPanelProps> = ({
  endpoints,
  apiKey,
  partnerKey,
  balloonId,
  onClose,
}) => {
  const service = useMemo(
    () => new AssistantService(endpoints, apiKey, partnerKey),
    [endpoints, apiKey, partnerKey]
  );

  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    service
      .createConversation(undefined, balloonId)
      .then((conversation) => {
        if (!cancelled) setThreadId(conversation.thread_id);
      })
      .catch(() => {
        if (!cancelled) setError('Could not start a conversation.');
      });
    return () => {
      cancelled = true;
    };
  }, [service, balloonId]);

  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeTool]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy || !threadId) return;

    setError(null);
    setDraft('');
    setBusy(true);
    setActiveTool(null);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

    const appendToAssistant = (chunk: string) => {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = { ...last, content: last.content + chunk };
        }
        return next;
      });
    };

    try {
      await service.streamChat({ threadId, message: text, balloonId }, undefined, (frame) => {
        if (frame.type === 'token') {
          appendToAssistant(frame.text);
        } else if (frame.type === 'tool') {
          setActiveTool(frame.name);
        } else if (frame.type === 'error') {
          setError(frame.message);
        }
      });
    } catch {
      setError('The assistant hit an error.');
    } finally {
      setBusy(false);
      setActiveTool(null);
    }
  }, [draft, busy, threadId, balloonId, service]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void send();
      }
    },
    [send]
  );

  return (
    <div
      className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col z-[100]"
      role="dialog"
      aria-label="Beat Engineer assistant"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100">Beat Engineer</div>
          <div className="text-xs text-gray-500">Read-only assistant{balloonId ? ' · this event' : ''}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close assistant"
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
        >
          ×
        </button>
      </div>

      <div ref={transcriptRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {messages.length === 0 && (
          <div className="text-gray-500 text-sm">
            {balloonId
              ? 'Ask about this event — when it was modified, why its quality score is what it is, whether it went through web enrichment or AI improve.'
              : 'Ask how the system works — configuring a zeppelin, partner or banner, the difference between merge and cluster, and more.'}
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={
              'max-w-[85%] rounded-lg px-3 py-2 text-sm break-words ' +
              (m.role === 'user'
                ? 'self-end whitespace-pre-wrap bg-blue-600 text-white'
                : 'self-start bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100')
            }
          >
            {m.role === 'assistant' ? (
              m.content ? (
                <MarkdownMessage content={m.content} />
              ) : busy ? (
                '…'
              ) : (
                ''
              )
            ) : (
              m.content
            )}
          </div>
        ))}

        {activeTool && (
          <div className="self-start text-xs text-gray-400">Looking things up ({activeTool})…</div>
        )}
      </div>

      {error && <div className="px-4 py-2 text-sm text-red-500">{error}</div>}

      <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex gap-2 items-stretch">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!threadId || busy}
          rows={1}
          placeholder="Ask the Beat Engineer…"
          className="flex-1 resize-none rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm p-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={!draft.trim() || busy || !threadId}
          className="flex items-center justify-center rounded bg-blue-600 text-white text-sm px-4 py-2 disabled:opacity-50"
        >
          {busy ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
};
